import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from './AuthContext';
import '../css/Login.css';

function OAuthRegister() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setTokensAndUser, updateGroups } = useAuth();

  const [code] = useState(() => searchParams.get('code'));
  const [username, setUsername] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'checking' | 'available' | 'unavailable' | 'invalid' | 'tooLong'
  const [registering, setRegistering] = useState(false);
  const debounceRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // URL에서 code 즉시 제거 (보안)
  useEffect(() => {
    window.history.replaceState({}, document.title, '/oauth/register');
  }, []);

  // code 없으면 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!code) {
      toast.error(t('oauthRegister.expired'));
      navigate('/login');
    }
  }, [code, navigate, t]);

  const checkUsername = useCallback(async (value) => {
    if (!value) {
      setUsernameStatus(null);
      return;
    }
    if (value.length > 50) {
      setUsernameStatus('tooLong');
      return;
    }
    if (!/^[a-zA-Z0-9_]+$/.test(value)) {
      setUsernameStatus('invalid');
      return;
    }

    setUsernameStatus('checking');
    try {
      const response = await fetch(`${backendUrl}/api/v1/auth/oauth/check-username`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: value })
      });
      const data = await response.json();
      if (data.success) {
        setUsernameStatus(data.available ? 'available' : 'unavailable');
      } else {
        setUsernameStatus('unavailable');
      }
    } catch {
      setUsernameStatus(null);
    }
  }, [backendUrl]);

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value) {
      setUsernameStatus(null);
      return;
    }

    debounceRef.current = setTimeout(() => {
      checkUsername(value);
    }, 500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username || usernameStatus !== 'available') {
      return;
    }
    if (!privacyConsent) {
      toast.error(t('privacy.consentRequired'));
      return;
    }

    setRegistering(true);
    try {
      const response = await fetch(`${backendUrl}/api/v1/auth/oauth/complete-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code, username, privacy_consent: privacyConsent })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        toast.error(data.message || t('oauthRegister.failed'));
        if (data.message && data.message.includes('expired')) {
          navigate('/login');
        }
        return;
      }

      // JWT 토큰 저장
      setTokensAndUser(data.accessToken);

      // 사용자 그룹 fetch
      try {
        const groupResponse = await fetch(`${backendUrl}/api/v1/user/user_groups`, {
          headers: {
            'Authorization': `Bearer ${data.accessToken}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include',
        });
        if (groupResponse.ok) {
          const groupData = await groupResponse.json();
          updateGroups(groupData.groups);
        }
      } catch {
        // 그룹 fetch 실패해도 로그인은 성공
      }

      toast.success(t('oauthRegister.success'));
      navigate('/');
    } catch {
      toast.error(t('oauthRegister.failed'));
    } finally {
      setRegistering(false);
    }
  };

  const getStatusMessage = () => {
    switch (usernameStatus) {
      case 'checking': return <span style={{ color: 'var(--text-tertiary)' }}>{t('oauthRegister.checking')}</span>;
      case 'available': return <span style={{ color: '#28a745' }}>{t('oauthRegister.available')}</span>;
      case 'unavailable': return <span style={{ color: '#dc3545' }}>{t('oauthRegister.unavailable')}</span>;
      case 'invalid': return <span style={{ color: '#dc3545' }}>{t('oauthRegister.invalidFormat')}</span>;
      case 'tooLong': return <span style={{ color: '#dc3545' }}>{t('oauthRegister.tooLong')}</span>;
      default: return null;
    }
  };

  if (!code) return null;

  return (
    <div className="login-container">
      <h2>{t('oauthRegister.title')}</h2>
      <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginBottom: '20px' }}>
        {t('oauthRegister.subtitle')}
      </p>

      <form onSubmit={handleSubmit}>
        <div>
          <label>{t('username')}</label>
          <input
            type="text"
            value={username}
            onChange={handleUsernameChange}
            placeholder={t('oauthRegister.usernamePlaceholder')}
            maxLength={50}
            required
            autoFocus
          />
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '4px' }}>
            {t('oauthRegister.usernameHelp')}
          </p>
          {usernameStatus && (
            <p style={{ fontSize: '13px', marginTop: '4px' }}>
              {getStatusMessage()}
            </p>
          )}
        </div>

        <div className="privacy-consent-group">
          <label className="privacy-consent-label">
            <input
              type="checkbox"
              checked={privacyConsent}
              onChange={(e) => setPrivacyConsent(e.target.checked)}
            />
            <span>{t('privacy.consent')}</span>
          </label>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" className="privacy-link">
            {t('privacy.viewPolicy')}
          </a>
        </div>

        <button
          type="submit"
          disabled={registering || usernameStatus !== 'available' || !privacyConsent}
        >
          {registering ? t('oauthRegister.registering') : t('oauthRegister.register')}
        </button>
      </form>
    </div>
  );
}

export default OAuthRegister;
